import { useState } from 'react';

import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';
import { reportMarkdownComponents } from '#client/components/utils/markdownComponents';

import * as Styled from '../../BusinessCostResults.style';

/**
 * Left sidebar panel displaying user profile information
 * Shows the business type, state, and industry from user selections
 * @param {object} props - Component props
 * @param {object} props.userProfile - User profile with business details
 * @param {string} props.userProfile.businessType - Business entity type (e.g., "LLC")
 * @param {string} props.userProfile.location - State of formation (e.g., "Texas")
 * @param {string} props.userProfile.businessIndustry - Business industry (e.g., "Residential Cleaning Service")
 * @param {Function} props.onStartOver - Handler for starting over
 * @param {boolean} [props.loading=false] - Whether action is in progress
 * @component
 */
function UserProfilePanel({ userProfile, onStartOver, loading = false }) {
  const [startOverLoading, setStartOverLoading] = useState(false);

  const handleStartOver = async () => {
    if (startOverLoading || loading) return;

    setStartOverLoading(true);
    if (onStartOver && !loading) {
      await onStartOver();
    }
    setStartOverLoading(false);
  };

  return (
    <Styled.ReportSidebar>
      <Styled.UserProfileSection>
        <Styled.ProfileField>
          <Styled.ProfileFieldLabel size="md">Type</Styled.ProfileFieldLabel>
          <Styled.ProfileFieldValue size="md" weight="medium">
            {userProfile?.businessType || '—'}
          </Styled.ProfileFieldValue>
        </Styled.ProfileField>

        <Styled.ProfileField>
          <Styled.ProfileFieldLabel size="md">State</Styled.ProfileFieldLabel>
          <Styled.ProfileFieldValue size="md" weight="medium">
            {userProfile?.location || '—'}
          </Styled.ProfileFieldValue>
        </Styled.ProfileField>

        <Styled.ProfileField>
          <Styled.ProfileFieldLabel size="md">Industry</Styled.ProfileFieldLabel>
          <Styled.ProfileFieldValue size="md" weight="medium">
            {userProfile?.businessIndustry || '—'}
          </Styled.ProfileFieldValue>
        </Styled.ProfileField>

        {userProfile?.citiesOfOperation && (
          <Styled.ProfileField>
            <Styled.ProfileFieldLabel size="md">Cities of Operation</Styled.ProfileFieldLabel>
            <Text style={{ fontWeight: '600' }}>
              <ReactMarkdown components={reportMarkdownComponents} rehypePlugins={[rehypeRaw]}>
                {userProfile.citiesOfOperation}
              </ReactMarkdown>
            </Text>
          </Styled.ProfileField>
        )}
      </Styled.UserProfileSection>

      <Styled.ButtonsContainer>
        <Button
          clickstreamProps={{
            ...CLICKSTREAM_COMMON_PROPS,
            elementName: 'business_cost_start_over',
            elementText: 'Start over'
          }}
          disabled={startOverLoading || loading}
          fullWidth
          loading={startOverLoading}
          size="medium"
          variant="outlined"
          onClick={handleStartOver}
        >
          Start over
        </Button>
      </Styled.ButtonsContainer>
    </Styled.ReportSidebar>
  );
}

UserProfilePanel.propTypes = {
  userProfile: PropTypes.shape({
    businessType: PropTypes.string,
    location: PropTypes.string,
    businessIndustry: PropTypes.string,
    citiesOfOperation: PropTypes.string
  }),
  onStartOver: PropTypes.func,
  loading: PropTypes.bool
};

export default UserProfilePanel;
